import AudioCubeApiCallerInstance from '../utils/AudioCubeApiCallerInstance';
import { EXPORT_DATA_TYPE, SOUNDBOX_API_ENDPOINTS } from '../URLConstants';

export const GetAllMerchants = async (pageNo, limit, searchBy, signal) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS.GET_MERCHANT_LIST}?page=${pageNo}&limit=${limit}`,
    {
      params: {
        where: JSON.stringify(searchBy)
      },
      ...(signal && { signal })
    }
  );
  return response;
};

export const GetMerchantById = async (merchantId) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS.GET_MERCHANT_LIST}/${merchantId}`
  );
  return response;
};

export const AddNewMerchant = async (payload) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS.ADD_NEW_MERCHANT,
    payload
  );
  return response;
};

export const MerchantBulkUpload = async (file) => {
  const formData = new FormData();
  formData.append('upload', file);
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS.MERCHANT_BULK_UPLOAD,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response;
};

export const GetMerchantTransactions = async (searchBy, pageNo, limit, fromDate, toDate) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS.GET_DEVICE_TRANSACTION}?page=${pageNo}&limit=${limit}`,
    {
      params: {
        where: JSON.stringify(searchBy),
        startDate: fromDate,
        endDate: toDate
      }
    }
  );
  return response;
};

export const UpdateMerchantStatus = async (vpa, status) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS.MERCHANT_STATUS_UPDATE,
    {
      status: [
        {
          vpa,
          status
        }
      ]
    }
  );
  return response;
};

export const ActivateMerchantAndAssignDevice = async (vpa, deviceImei) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS.ACTIVATE_MERCHANT_BY_ASSIGN_DEVICE,
    {
      mappings: [
        {
          vpa,
          deviceImei
        }
      ]
    }
  );
  return response;
};

export const GetMerchantTransactionStats = async (fromDate, toDate, vpa) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS.TRANSACTION_STATS, {
    params: {
      startDate: fromDate,
      endDate: toDate,
      vpa
    }
  });
  return response;
};

export const UpdateMerchant = async (merchantId, payload) => {
  const response = await AudioCubeApiCallerInstance.patch(
    `${SOUNDBOX_API_ENDPOINTS.ADD_NEW_MERCHANT}/${merchantId}`,
    payload
  );
  return response;
};

export const ExportMerchantReportWithFilter = async (startDate, endDate) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS.EXPORT_DATA_URL, {
    params: {
      type: EXPORT_DATA_TYPE?.TRANSACTION_EXPORT_REPORT,
      startDate,
      endDate
    },
    responseType: 'blob'
  });
  return response;
};

export const ExportMerchantOnboardingReportWithFilter = async (startDate, endDate) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS.EXPORT_DATA_URL, {
    params: {
      type: EXPORT_DATA_TYPE?.MERCHANT_ONBOARDING_REPORT,
      startDate,
      endDate
    },
    responseType: 'blob'
  });
  return response;
};

export const ExportMerchantTransactionHistoryReport = async (vpa, startDate, endDate) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS.EXPORT_DATA_URL, {
    params: {
      type: EXPORT_DATA_TYPE?.MERCHANT_TRANSACTION_HISTORY_REPORT,
      vpa,
      startDate,
      endDate
    },
    responseType: 'blob'
  });
  return response;
};

export const GetMerchantApprovalList = async (page, limit, where) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS?.MERCHANT_APPROVAL}?page=${page}&limit=${limit}`,
    {
      params: {
        where: JSON.stringify(where)
      }
    }
  );
  return response;
};

export const HandleApprovalRejection = async (payload) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS.MERCHANT_APPROVAL,
    payload
  );
  return response;
};

export const GetTaxonomyDetails = async (taxonomyId) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS?.GET_BRANCH_FOR_NON_ADMIN}/${taxonomyId}`
  );
  return response;
};

export const CheckDuplicatesForMerchantVerification = async (rowIds) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS?.CHECK_DUPLICATES_FOR_VERIFICATION,
    {
      rowIds
    }
  );
  return response;
};

// --- Merchant VPAs (merchant list + verification queue) ----------------------------

export const GetMerchantVpas = async (page, limit, where) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS?.MERCHANT_VPA, {
    params: {
      page,
      limit,
      where: JSON.stringify(where)
    }
  });
  return response;
};

export const GetMerchantVpaById = async (id) => {
  const response = await AudioCubeApiCallerInstance.get(
    `${SOUNDBOX_API_ENDPOINTS?.MERCHANT_VPA}/${id}`
  );
  return response;
};

// payload: { ids: [...], status: 'APPROVED' | 'REJECTED', remarks? }
export const UpdateMerchantVpaApprovalStatus = async (payload) =>
  AudioCubeApiCallerInstance.patch(SOUNDBOX_API_ENDPOINTS?.MERCHANT_VPA_APPROVAL, payload);

// Body is `{ phones: [...] }`; response is `{ matches: [{ value, records }] }`.
// Returns the matches array (empty on any unexpected shape).
export const CheckMerchantVpaDuplicates = async (phones) => {
  const response = await AudioCubeApiCallerInstance.post(
    SOUNDBOX_API_ENDPOINTS?.MERCHANT_VPA_DUPLICATE_CHECK,
    { phones }
  );
  return Array.isArray(response?.data?.matches) ? response.data.matches : [];
};

export const UnMapMerchant = async (vpa) => {
  const response = await AudioCubeApiCallerInstance.post(SOUNDBOX_API_ENDPOINTS?.UNMAP_MERCHANT, {
    status: [
      {
        vpa,
        status: 'deactivate'
      }
    ]
  });
  return response;
};

export const GetInactiveMerchantReport = async (query, startDate, endDate) => {
  const response = await AudioCubeApiCallerInstance.get(SOUNDBOX_API_ENDPOINTS?.MERCHANT_INACTIVE_REPORTS, {
    params: {
      query,
      startDate,
      endDate
    }
  });
  return response;
}